import { Trans } from "@lingui/react/macro";
import React from "react";
import * as styles from "./CruiseInfoSummary2.module.scss";
import {
  Button,
  Icon,
  isSanityImagePresent,
  PaginationButton,
  PromoBadges,
  ResponsiveSanityImage,
  SanityImageRatio,
  SectionContainer
} from "@silversea-ssc/ui-components-react";
import { FacebookShareButton } from "react-share";
import CruiseRecapColumns from "./CruiseRecap/CruiseRecapColumns";

import FeatureCard from "./FeatureCard/FeatureCard";
import { isPresent } from "src/utils/checks";
import { dequal } from "dequal/lite";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import {
  CruiseMapFragment,
  ECommerceBenefitsContentFragment,
  ThemedCruiseFragment
} from "types/graphql-types";
import ClientOnly from "components/ClientOnly";
import {
  ActivePromoForCruise,
  activePromosToPromoBadges
} from "src/utils/promos";
import PriceRecap from "./PriceRecap";
import ECommerceButtons from "components/ECommerceButtons";
import { CruisePriceAndOffersV2Response } from "src/ssc-api/prices/useCruisePrice";
import MaybeECommerceBenefits from "components/ECommerceBenefits/MaybeECommerceBenefits";
import { useClientSideFeatureFlag } from "src/utils/feature-flags";

interface CruiseInfoSummaryProps {
  map?: CruiseMapFragment;
  cruiseType?: string;
  cruiseCode: string;
  cruiseName: string;
  prices?: CruisePriceAndOffersV2Response;
  destinationName?: string;
  destinationFullPath?: string;
  shipName?: string;
  departureDate?: string;
  arrivalDate?: string;
  duration?: number;
  nextCruisePathByShip?: string;
  prevCruisePathByShip?: string;
  pagePath: string;
  pdfPath: string;
  raqUrl: string | undefined;
  ecommerceStatus: ECommerceStatus;
  themedCruises: ThemedCruiseFragment[];
  cruiseTags: string[];
  activePromos?: ActivePromoForCruise[];
  eCommerceBenefitsContent: ECommerceBenefitsContentFragment | undefined;
}

const CruiseInfoSummary: React.FC<
  React.PropsWithChildren<CruiseInfoSummaryProps>
> = ({
  map,
  cruiseName,
  prices,
  cruiseType,
  cruiseCode,
  destinationFullPath,
  destinationName,
  shipName,
  arrivalDate,
  departureDate,
  duration,
  nextCruisePathByShip,
  prevCruisePathByShip,
  pagePath,
  pdfPath,
  raqUrl,
  ecommerceStatus,
  themedCruises,
  children,
  activePromos = [],
  eCommerceBenefitsContent,
  cruiseTags
}) => {
  const comboShipBoardCredit = prices?.tokens?.["combo_ship_board_credit"];
  const promoBadges = activePromosToPromoBadges(activePromos);
  const isCruiseTagsEnabled = useClientSideFeatureFlag("cruiseTags");

  return (
    <SectionContainer
      backgroundColor="productTypeColor03"
      spacingBottom={null}
      dataContentName="cruise_summary"
    >
      <div className={styles.root} data-content-name="cruise_info_summary">
        <div className={styles.backTo}>
          <Button
            variant="inline-text"
            renderAs="a"
            href={destinationFullPath}
            dataContentPiece="view_all_cruises_topleft"
          >
            <Trans>
              Back to all{" "}
              <h3 className={styles.backToDestinationTag}>
                {destinationName} Cruises
              </h3>
            </Trans>
          </Button>
        </div>

        {isPresent(map) && isSanityImagePresent(map.image) && (
          <SanityImageRatio
            ratio={map.type == "Map" ? "auto" : "card-compact"}
            image={map.image}
            className={styles.mapContainer}
          >
            <ResponsiveSanityImage
              image={map.image}
              defaultWidth={450}
              defaultHeight={520}
              className={styles.map}
              alt={cruiseName}
              fit={map.type != "CruiseCard" ? "original" : undefined}
              loading="eager"
            />
          </SanityImageRatio>
        )}

        <div className={styles.titleInfo}>
          <h1 className={styles.cruiseName}>{cruiseName}</h1>
          {cruiseType == "Expedition" && (
            <div className={styles.cruiseExpedition}>Expedition cruise</div>
          )}
          <div className={styles.cruiseCode}>
            <Trans>Voyage Number</Trans> #{cruiseCode}
          </div>
        </div>

        <div className={styles.cruiseRecap}>
          <CruiseRecapColumns
            shipName={shipName}
            arrivalDate={arrivalDate}
            departureDate={departureDate}
            duration={duration}
            cruiseTags={isCruiseTagsEnabled ? cruiseTags : []}
          />
        </div>

        <div
          className={
            themedCruises.length > 0 && !isCruiseTagsEnabled
              ? styles.featuresContainer
              : styles.featuresContainerPlaceholder
          }
        >
          <div className={styles.featuresList}>
            {!isCruiseTagsEnabled &&
              themedCruises.map((feature) => (
                <FeatureCard
                  key={feature.code}
                  code={feature.code}
                  name={feature.name?.localized}
                />
              ))}
          </div>
        </div>

        <div className={styles.promoBadgesContainer}>
          {promoBadges.length > 0 && (
            <React.Fragment>
              <div className={styles.promoBadgesTitle}>
                <Trans>Featured offers</Trans>
              </div>
              <PromoBadges
                promos={promoBadges}
                className={styles.promoBadges}
              />
            </React.Fragment>
          )}
        </div>

        <PriceRecap
          className={styles.priceRecap}
          comboShipBoardCredit={comboShipBoardCredit}
          prices={prices}
        />

        <div className={styles.ctaContainers}>
          <div className={styles.ctaButtons}>
            <ECommerceButtons
              url={ecommerceStatus}
              size="regular"
              raqUrl={raqUrl}
            />
          </div>

          <MaybeECommerceBenefits
            {...eCommerceBenefitsContent}
            prices={prices}
            cruiseCode={cruiseCode}
            ecommerceStatus={ecommerceStatus}
          />
        </div>

        <div className={styles.serviceButtons}>
          <ClientOnly>
            <FacebookShareButton
              url={`https://www.silversea.com${pagePath}`}
              className={styles.facebookShareButton}
              data-content-piece="share_facebook"
              aria-label="Share on Facebook"
            >
              <Icon name="facebook" size="1.5x" />
            </FacebookShareButton>
          </ClientOnly>
          <ClientOnly>
            <Button
              className={styles.pdfButton}
              variant="elevated"
              href={pdfPath}
              target="_blank"
              iconName="pdf"
              download
              dataContentPiece="download_cruise_pdf"
              rel="noreferrer nofollow"
              aria-label="Download PDF"
            ></Button>
          </ClientOnly>
        </div>
        <div className={styles.cruisePagination}>
          <div className={styles.cruisePaginationLabel}>
            <Trans>
              previous/next cruise <br /> with {shipName}
            </Trans>
          </div>
          <PaginationButton
            disabled={!prevCruisePathByShip}
            arrow="left"
            href={prevCruisePathByShip}
            dataContentPiece="prev_cruise"
            aria-label="Previous Cruise"
          ></PaginationButton>
          <PaginationButton
            disabled={!nextCruisePathByShip}
            arrow="right"
            href={nextCruisePathByShip}
            dataContentPiece="next_cruise"
            aria-label="Next Cruise"
          ></PaginationButton>
        </div>
      </div>
      {children}
    </SectionContainer>
  );
};

export default React.memo(CruiseInfoSummary, dequal);
